import { useMemo, useState } from 'react';
import { Pane, PaneHead } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useMyDay, usePerformed, type MyDay, type PerformedRow } from './api';
import { clock, dayMonth, longDay, money, shortDay, shortName } from './format';
import { TechModal } from './components/TechModal';
import { PhoneScreen } from './components/PhoneScreen';
import { useTechOverlays } from './components/TechOverlays';

type Tab = 'today' | 'month';

const avgText = (m: number | null | undefined) => (m == null ? '—' : m < 1 ? '<1 min' : `${m} min`);

const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
const word = (n: number) => WORDS[n] ?? String(n);

/** "Commission 8% of OCT · 5% of Refraction" */
function dealLine(day: MyDay | undefined) {
  const rates = day?.rates ?? [];
  if (!rates.length) return null;
  return rates.map((r) => `${r.percent}% of ${r.label}`).join(' · ');
}

/**
 * Technician · My day — `g_day`, phone `g_t4`. THE COUNT THAT PAYS.
 *
 * Read-only, your own tally: every send stamps performed-by, and that stamp is
 * the only input to the month statement. The commission rate is printed where
 * the count is, so the number and the deal are never on two different screens.
 */
export function TechDayPage() {
  const day = useMyDay();
  const [tab, setTab] = useState<Tab>('today');
  const [picked, setPicked] = useState<string | null>(null);
  const [statement, setStatement] = useState(false);
  usePageMeta({ title: 'My day' });
  const ov = useTechOverlays();

  const d = day.data;
  const rows = useMemo(() => d?.byKind ?? [], [d]);
  const sel = rows.find((r) => r.kind === picked) ?? rows[0] ?? null;
  const deal = dealLine(d);
  const stats = tab === 'today'
    ? [
      { v: d?.today.performed ?? 0, l: 'Performed today' },
      { v: d?.today.retakes ?? 0, l: 'Retakes' },
      { v: d?.today.pending ?? 0, l: 'Pending' },
      { v: avgText(d?.today.avgMinutes), l: 'Avg per test' },
    ]
    : [
      { v: d?.month.performed ?? 0, l: 'Performed this month' },
      { v: d?.month.retakes ?? 0, l: 'Retakes' },
      { v: d?.today.pending ?? 0, l: 'Pending' },
      { v: avgText(d?.month.avgMinutes), l: 'Avg per test' },
    ];

  const sig = `${sel?.kind ?? '-'}:${sel?.today ?? 0}:${sel?.month ?? 0}|${d?.today.performed ?? -1}|${deal ?? ''}|${tab}`;

  useShellChrome(
    {
      raw: true,
      title: 'My day',
      sub: `${longDay()} · read-only — your own tally`,
      search: 'Search your performed records…',
      onSearch: ov.openSearch,
      onBell: ov.openBell,
      primary: { label: 'Open my month statement', onClick: () => setStatement(true) },
      pane: (
        <Pane
          head={<PaneHead overline="Selected test" title={sel?.label ?? 'Nothing performed yet'} sub="your own tally — read-only" />}
          foot={<button type="button" className="btn btn-outline grow" onClick={() => setStatement(true)}>Open my month statement</button>}
        >
          {sel ? (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div className="statcard"><p className="sv">{sel.today}</p><p className="sl">Today</p></div>
                <div className="statcard"><p className="sv">{sel.month}</p><p className="sl">This month</p></div>
              </div>
              <div className="card">
                <p className="t-ovl mut">How it counts</p>
                <p className="t-sec sec mt8">
                  {sel.today} send{sel.today === 1 ? '' : 's'} today, each one an attached study with your name on it.
                  {d && d.today.retakes ? ` ${word(d.today.retakes)} of today’s ${d.today.performed} came back as repeats and were redone.` : ''}
                </p>
              </div>
              {sel.ratePercent != null ? (
                <div className="card">
                  <p className="t-ovl mut">Commission</p>
                  <p className="t-sec sec mt8">
                    {sel.ratePercent}% of {sel.label}{sel.earnedMonth != null ? ` · ${money(sel.earnedMonth)} this month` : ''}
                  </p>
                </div>
              ) : null}
            </>
          ) : (
            <div className="emptystate"><p className="t-sec">Each send lands here with your name on it.</p></div>
          )}
        </Pane>
      ),
      phone: <PhoneDay day={d} />,
    },
    [sig],
  );


  return (
    <div className="maie desk tech-root">
        {ov.overlays}
      <div className="dcontent">
        <div className="tabbar" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'today'} className={`tb${tab === 'today' ? ' on' : ''}`} onClick={() => setTab('today')}>Today</button>
          <button type="button" role="tab" aria-selected={tab === 'month'} className={`tb${tab === 'month' ? ' on' : ''}`} onClick={() => setTab('month')}>This month</button>
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          {stats.map((s) => (
            <div key={s.l} className="statcard"><p className="sv">{s.v}</p><p className="sl">{s.l}</p></div>
          ))}
        </div>
          <table className="dtable">
            <thead>
              <tr>
                <th style={{ width: '46%' }}>Test</th>
                <th style={{ width: '26%' }}>Today</th>
                <th style={{ width: '28%' }}>This month</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.kind} className={`rowc${sel?.kind === r.kind ? ' sel' : ''}`} onClick={() => setPicked(r.kind)}>
                  <td><span className="t-body w6">{r.label}</span></td>
                  <td><span className="t-body w6">{r.today}</span></td>
                  <td><span className="t-sec sec">{r.month}</span></td>
                </tr>
              ))}
              {d && rows.length === 0 ? (
                <tr className="rowc">
                  <td colSpan={3}><span className="t-sec mut">Nothing performed yet this month — each send lands here with your name on it.</span></td>
                </tr>
              ) : null}
            </tbody>
          </table>
        <div className="card card-flat mt16">
          <p className="t-ovl mut">How this becomes money</p>
          <p className="t-sec sec mt8">
            {deal ? <>Commission <span className="w6">{deal}</span> — </> : <>No commission rate on file yet — </>}
            the rate is printed where the count is, so the number and the deal are never on two different screens.
          </p>
          <p className="t-sec sec mt8">
            My month → the commission statement the accountant prepares; the performed-by stamps are the only input.
          </p>
        </div>
      </div>
      <StatementModal open={statement} onClose={() => setStatement(false)} />
    </div>
  );
}

function PerformedTable({ rows, loading, empty }: { rows: PerformedRow[]; loading?: boolean; empty: string }) {
  return (
    <table className="dtable">
      <thead>
        <tr>
          <th style={{ width: '18%' }}>When</th>
          <th style={{ width: '34%' }}>Test</th>
          <th style={{ width: '20%' }}>Patient</th>
          <th style={{ width: '14%' }}>Price</th>
          <th style={{ width: '14%' }}>Commission</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="rowc">
            <td><span className="t-sec sec">{dayMonth(r.completedAt)} · {clock(r.completedAt)}</span></td>
            <td>
              <p className="t-body w6">{r.title}</p>
              {r.repeatOfId ? <p className="t-cap mut">repeat — redone</p> : null}
            </td>
            <td><span className="t-sec sec">{shortName(r.patient)}</span></td>
            <td><span className="t-sec sec">{money(r.price)}</span></td>
            <td><span className="t-sec sec">{r.commission != null ? money(r.commission) : '—'}</span></td>
          </tr>
        ))}
        {!loading && rows.length === 0 ? (
          <tr className="rowc"><td colSpan={5}><span className="t-sec mut">{empty}</span></td></tr>
        ) : null}
      </tbody>
    </table>
  );
}

/** THE MONTH STATEMENT — every performed record this month, priced where the catalog prices it. */
function StatementModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const rows = usePerformed({ enabled: open });
  const list = rows.data ?? [];
  const total = list.reduce((s, r) => s + (r.commission ?? 0), 0);
  const priced = list.filter((r) => r.commission != null).length;
  const month = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  return (
    <TechModal open={open} onClose={onClose} width={820} title={`My month — ${month}`}
      sub={`${list.length} performed · the performed-by stamps, as the accountant reads them`}
      foot={<button type="button" className="btn btn-outline" onClick={onClose}>Close</button>}>
      <div style={{ marginTop: 12, maxHeight: '60vh', overflowY: 'auto' }}>
        <PerformedTable rows={list} loading={rows.isLoading} empty="Nothing performed yet this month." />
      </div>
      <p className="t-sec sec mt12">
        {priced ? <>Commission this month: <span className="w6">{money(total)}</span> across {priced} priced test{priced === 1 ? '' : 's'}.</>
          : 'No commission is computed yet — a rate and a catalog price are both needed.'}
      </p>
    </TechModal>
  );
}

/* ─────────────────────────── phone · g_t4 ─────────────────────────── */

function PhoneDay({ day }: { day: MyDay | undefined }) {
  const [open, setOpen] = useState(false);
  const rows = usePerformed({ enabled: open });
  return (
    <PhoneScreen door="myday" title={`My day · ${shortDay()}`} trailing={<span className="chip chip-outline chip-sm">read-only</span>}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { l: 'Performed today', v: day?.today.performed ?? 0 },
          { l: 'Retakes', v: day?.today.retakes ?? 0 },
          { l: 'Pending', v: day?.today.pending ?? 0 },
        ].map((s) => (
          <div key={s.l} className="card" style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">{s.l}</p>
            <p className="t-heading mt4">{s.v}</p>
          </div>
        ))}
      </div>
      <div className="card">
        {(day?.byKind ?? []).map((k, i) => (
          <div key={k.kind} className={`flex${i ? ' mt4' : ''}`} style={{ justifyContent: 'space-between' }}>
            <p className="t-body">{k.label}</p>
            <p className="t-strong">{k.today}</p>
          </div>
        ))}
        {day && day.byKind.length === 0 ? <p className="t-sec mut">Nothing performed yet — each send lands here with your name on it.</p> : null}
      </div>
      {dealLine(day) ? <p className="t-sec sec center mt12">Commission {dealLine(day)}</p> : null}
      <p className="t-sec mut center mt16">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
          My month → commission statement (accountant’s payout screen)
        </button>
      </p>
      {open ? (
        <div className="card mt12">
          {(rows.data ?? []).map((r) => (
            <div key={r.id} className="flex mt4" style={{ justifyContent: 'space-between', gap: 8 }}>
              <p className="t-sec">{dayMonth(r.completedAt)} · {r.title}</p>
              <p className="t-sec sec">{r.commission != null ? money(r.commission) : '—'}</p>
            </div>
          ))}
          {rows.data && rows.data.length === 0 ? <p className="t-sec mut">Nothing performed yet this month.</p> : null}
        </div>
      ) : null}
    </PhoneScreen>
  );
}
